import Bounds from 'apollo-e/utils/Bounds';
import Texture, { Texturable, SpriteTexture } from '../rendering/Texture';
import BoxPartitioning from './BoxPartitioning';

type onResizeType = (textureWidth: number, textureHeight: number) => void;

// TODO: use ifdef
const debug = false;
let canvases;
if (debug) {
	canvases = {};
}

let dynamicTextureID = 0;
const textureDimIncrement = 128;
const initialSize = textureDimIncrement;
const maxDynamicTextureDim = 2048;
export default class DynamicTexture {
	private _id: number;
	private _margin: number;
	texture: Texture;
	spriteTextures: Array<SpriteTexture>;
	_onResize: Array<onResizeType>;

	texturePartitioning: BoxPartitioning;

	constructor (margin: number) {
		this._id = dynamicTextureID++;
		this._margin = margin;

		if (debug) {
			const canvas = document.createElement('canvas');
			canvas.style.position = 'absolute';
			canvas.style.top = '0px';
			canvas.style.left = '0px';
			canvas.style.width = (maxDynamicTextureDim / 2) + 'px';
			canvas.style.height = (maxDynamicTextureDim / 2) + 'px';
			canvas.width = maxDynamicTextureDim;
			canvas.height = maxDynamicTextureDim;
			canvases[this._id] = canvas;
			document.body.appendChild(canvas);
		}

		this.texturePartitioning = new BoxPartitioning(0, maxDynamicTextureDim, 0, maxDynamicTextureDim);

		const priorityZone = this.texturePartitioning.priorityZone;
		priorityZone.left = 0;
		priorityZone.right = initialSize;
		priorityZone.top = 0;
		priorityZone.bottom = initialSize;

		this.texture = new Texture(null, {
			width: initialSize,
			height: initialSize
		});

		this.texture.name = 'dynamic_texture';
		this.spriteTextures = [];

		this._onResize = [];
	}

	addImage (image: Texturable) {
		const texturePosition = this.texturePartitioning.add(image.width, image.height);
		if (texturePosition === null) {
			throw new Error('Cannot add image to texture!');
		}

		// do we need to adjust priority zone?
		const priorityZone = this.texturePartitioning.priorityZone;
		const occupiedBounds = this.texturePartitioning.occupiedBounds;
		while (priorityZone.right < occupiedBounds.right) {
			// need to extend to the right
			priorityZone.right += textureDimIncrement;
		}

		while (priorityZone.bottom < occupiedBounds.bottom) {
			// need to extend to the bottom
			priorityZone.bottom += textureDimIncrement;
		}

		const textureWidth = priorityZone.width;
		const textureHeight = priorityZone.height;
		// console.error('textureWidth', textureWidth + 'x' + textureHeight, this.texture.width + 'x' + this.texture.height);
		if (textureWidth > this.texture.width || textureHeight > this.texture.height) {
			this.texture.setDimensions(textureWidth, textureHeight, true);

			// Need to update all sprite textures to new dimension
			for (let s = 0; s < this.spriteTextures.length; s++) {
				this.spriteTextures[s].resetCoordinates();
			}

			for (let r = 0; r < this._onResize.length; r++) {
				this._onResize[r](textureWidth, textureHeight);
			}
			
			if (debug) {
				const canvas = canvases[this._id];
				const context = canvas.getContext('2d');
				const savedImage = context.getImageData(0, 0, canvas.width, canvas.height);
				canvas.style.width = (textureWidth / 2) + 'px';
				canvas.style.height = (textureHeight / 2) + 'px';
				canvas.width = textureWidth;
				canvas.height = textureHeight;
				context.putImageData(savedImage, 0, 0);
			}
		}

		if (debug) {
			const canvas = canvases[this._id];
			const context = canvas.getContext('2d');
			// context.putImageData(image, texturePosition.left, texturePosition.top);
			context.putImageData(image, texturePosition.left, texturePosition.top);
		}

		// Need to upload image to texture position
		this.texture.addToUpdateList(image, texturePosition);
		// console.error('addToUpdateList', texturePosition);

		const spriteTexture = new SpriteTexture(this.texture, new Bounds(texturePosition.left + this._margin, texturePosition.right - this._margin, texturePosition.top + this._margin, texturePosition.bottom - this._margin));
		this.spriteTextures.push(spriteTexture);

		return spriteTexture;
	}

	dismissArea (area: Bounds) {
		this.texturePartitioning.free(new Bounds(area.left - this._margin, area.right + this._margin, area.top - this._margin, area.bottom + this._margin));
	}

	onResize (onResize: onResizeType) {
		this._onResize.push(onResize);
	}
}
